import { useClosedLiftScopes } from '../../api/hooks/useLiftRules';
import { hasNoRoute, noRouteWords } from '../../state/lift-rule-waiting';
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RobotState } from '../../api/rmf-client';
import { StatusChip } from '../../design-system/components/StatusChip';
import { FormDialog } from '../../design-system/components/FormDialog';
import {
  DIALOG_FIELDSET,
  DIALOG_LEGEND,
  DIALOG_LIST,
  DIALOG_OPTION,
  DIALOG_OPTION_HINT,
  DIALOG_OPTION_LABEL,
} from '../../design-system/components/dialog-styles';
import type { StatusKind } from '../../design-system/tokens';
import { formatDateTime } from '../../utils/date-format';
import { useTasks } from '../../api/hooks';
import {
  ESTOP_RELOCALIZE_ISSUE,
  FIRE_RELOCALIZE_ISSUE,
  commissionKey,
  useCommissionRobot,
  useRobotCommissionStates,
  useRobotIdlePolicies,
  useSetRobotIdleBehavior,
} from '../../api/hooks/useCommissionRobot';
import { capabilitiesQueryOptions } from '../../api/query-options';
import { ApiClientError, networkFailureText } from '../../api/v1-client';
import { useActiveSite, useSafeSiteId } from '../../state/useActiveSite';
import { RoleGuard } from '../../auth/RoleGuard';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { ROBOT_FIRE_RELOCALIZE_ROLES, ROBOT_IDLE_BEHAVIOR_ROLES } from '../../auth/types';
import type {
  CapabilityId,
  CommandRecord,
  PendingDirectTasksPolicy,
  PendingDispatchTasksPolicy,
  RobotCommissionState,
  RobotIdlePolicy,
  SiteId,
} from '../../api/v1';
import { TaskControlActions } from '../Tasks/TaskControlActions';
import { heldLift, ROBOT_STATE_MAX_AGE_MS } from '../Tasks/task-ownership';
import { useLifts } from '../../api/hooks/useLifts';
import { DustbagConfirmAction } from './DustbagConfirmAction';
import { FireRelocalizeAction } from './FireRelocalizeAction';
import {
  OUT_OF_LIFT_THEN_QR,
  QR_SCAN_STEPS,
  SCANNED_IT_BUTTON,
  STILL_LOST_CALL,
} from '../../state/attention/robot-copy';
import { isConfirmableDustbagTask } from './dustbag-confirm';
import { TaskPhasesList } from '../Tasks/TaskPhasesList';
import { TaskElapsed, TaskRemaining } from '../Tasks/TaskTiming';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import { ShowWhereLink } from '../../shell/ShowWhereLink';
import { useFeedHealth } from '../../api/feed-health';
import { robotReceiptKey, useRobotReceipts } from '../../api/robot-receipts';
import { useNowTick } from '../../state/clock';
import { useRobotPresence } from '../Map/useRobotPresence';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { formatZonedClock } from '../Tasks/zoned-time';
import { formatClock } from '../Devices/lift-reading';
import {
  batteryText,
  heardText,
  heardTickMs,
  isReportingNow,
  lastReportAtMs,
  lastReportText,
  locationText,
  mapPositionDetail,
  needsLocalising,
} from './robot-lines';
import './RobotDetailDrawer.css';
// Both stylesheets are global and already reach this drawer through
// the components it renders (`TaskControlActions` imports the first).
// Imported explicitly because this file now uses their classes
// directly, and a transitive import is not a contract.
import '../Tasks/TaskDetailDrawer.css';
import '../../design-system/components/ConfirmDialog.css';

const ROBOT_STATUS_MAP: Record<string, StatusKind> = {
  active: 'active',
  idle: 'idle',
  error: 'error',
  charging: 'charging',
  warning: 'warning',
  offline: 'offline',
};

const TASK_STATUS_MAP: Record<string, StatusKind> = {
  queued: 'idle',
  active: 'active',
  completed: 'completed',
  failed: 'error',
  cancelled: 'warning',
  interrupted: 'warning',
};

/**
 * Commission state reuses the existing status palette rather than
 * introducing a colour of its own (`design-system/tokens.ts`).
 *
 * `completed` (blue) for commissioned — the calm "normal, nothing to
 * do here" tone, and deliberately NOT `active` (cyan), which the robot
 * status chip a few rows up already uses for "currently executing";
 * two cyan chips on one panel meaning different things is exactly the
 * confusion that token's own comment warns about.
 *
 * `warning` (amber) for decommissioned: an operator-visible abnormal
 * state that is not a fault — the robot is healthy, it has been
 * deliberately stood down.
 */
const COMMISSION_STATUS_MAP: Record<RobotCommissionState, StatusKind> = {
  commissioned: 'completed',
  decommissioned: 'warning',
  unknown: 'offline',
};

/** The chip a robot that has lost its place on the map shows in place of
 *  its status. `ROBOT_STATUS_MAP` has no `pending-localize`, so it used to
 *  fall through to OFFLINE: the wrong problem, and the wrong person sent
 *  to fix it. The robot is on and reporting; it cannot say where it is. */
const LOCALIZE_CHIP_LABEL = 'LOST ITS PLACE';
const LOCALIZE_CHIP_TITLE =
  'The robot is on and reporting, but cannot tell where it is on the map. ' +
  'It cannot take work until someone sets its position again.';

const COMMISSION_LABEL: Record<RobotCommissionState, string> = {
  commissioned: 'COMMISSIONED',
  decommissioned: 'DECOMMISSIONED',
  unknown: 'UNKNOWN',
};

/** Roles that hold `robot.commission`/`robot.decommission`
 *  (`policy/capabilities.py::ROLE_TO_CAPABILITIES`). */
const COMMISSION_ROLES = ['nexsoss:platform_admin', 'nexsoss:site_supervisor'] as const;

/**
 * How long automatic charging may stay off, offered in minutes.
 *
 * Every choice is finite because switching automatic charging off also
 * switches off RMF's low-battery retreat to the charger
 * (`TaskManager::consider_retreating_to_charger` refuses once the idle
 * behaviour is disabled), so a robot forgotten in this state sits
 * where it is until it is flat. Only a platform admin may choose the
 * open-ended option, and it is labelled for what it is rather than
 * hidden as an empty default.
 */
const AUTO_CHARGE_TTL_CHOICES: ReadonlyArray<{ minutes: number; label: string }> = [
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 240, label: '4 hours' },
  { minutes: 480, label: '8 hours' },
  { minutes: 720, label: '12 hours' },
];

const AUTO_CHARGE_DEFAULT_TTL_MINUTES = 120;

/** The role the api-server requires before it will accept an
 *  open-ended "off" (`enabled: false` with no `ttl_minutes`). Drawing
 *  the option for anyone else produces a 400 they cannot act on. */
const OPEN_ENDED_TTL_ROLE = 'nexsoss:platform_admin';

/** `14:35` on the site's clock, as the calendar reads it (2026-10-31:
 *  it used the device's clock, so a laptop in another zone showed a
 *  different "until" from the calendar's). Deliberately time-only: the
 *  longest TTL on offer is 12 hours, so a date would be noise on every
 *  chip but the rare one that crosses midnight. */
function clockTime(epochMs: number, timeZone: string): string {
  if (!Number.isFinite(epochMs)) return '—';
  return formatZonedClock(epochMs, timeZone);
}

/**
 * The automatic-charging chip, which shows DESIRED state against LIVE
 * state rather than either one alone.
 *
 * They diverge routinely and the divergence is the interesting part:
 * the fleet adapter holds the flag in RAM and resets it to true on
 * every process restart, so "the operator asked for off, RMF currently
 * says on" is a real, recurring state that a reconciler is in the
 * middle of correcting. Showing only the stored row would claim a
 * robot is not charging when it is already driving to the dock;
 * showing only the live flag would lose the operator's intent the
 * moment the adapter bounced. `applying…` is the honest word for both
 * mismatches.
 *
 * A live `null` (no `commission` object published at all, e.g. a
 * worker older than 0.4.11) is NOT treated as a mismatch: with no row
 * it reads ON, which is upstream's own default.
 */
function autoChargeChip(
  policy: RobotIdlePolicy | null,
  live: boolean | null,
  settled: boolean,
  timeZone: string,
): { status: StatusKind; label: string; pulse: boolean } {
  // Nothing is known until the stored rows have answered, and this
  // query is mounted by this drawer alone -- so it is cold on the first
  // open of every robot. Without this branch the operator sees a
  // confident `ON` for a full round trip before it flips to
  // `OFF · until 14:30`, which is the one reading of this chip that
  // must never be wrong: it is what tells them whether the robot is
  // about to take itself to the lift. The same honesty rule the hook
  // applies to a `null` live flag ("no signal" is not "on").
  if (!settled) return { status: 'offline', label: '…', pulse: false };
  if (policy) {
    if (live === false) {
      return {
        status: 'warning',
        label:
          policy.expires_at_ms == null
            ? 'OFF · until turned on'
            : `OFF · until ${clockTime(policy.expires_at_ms, timeZone)}`,
        pulse: false,
      };
    }
    return { status: 'warning', label: 'OFF · applying…', pulse: true };
  }
  if (live === false) return { status: 'warning', label: 'ON · applying…', pulse: true };
  return { status: 'completed', label: 'ON', pulse: false };
}

/** The drawer's four confirms. Only one may be open at a time, which
 *  is why this is one value rather than four flags. */
type Dialog = 'commission' | 'decommission' | 'autocharge-off' | 'autocharge-on' | null;

export function CommissionBadge({ state }: { state: RobotCommissionState }) {
  return <StatusChip status={COMMISSION_STATUS_MAP[state]} label={COMMISSION_LABEL[state]} />;
}

/**
 * The decommission dialog's "Queued tasks" choice, and the TWO policies
 * each choice actually sends.
 *
 * RMF queues two kinds of work per robot and takes a separate policy
 * for each: dispatched tasks (assigned by the fleet's own allocator,
 * and therefore reassignable) and direct tasks (addressed to this robot
 * by name, and therefore not). This dialog offers ONE choice because an
 * operator standing a robot down has one intention — "hand the work
 * over", "let it finish", "drop it" — so each option carries the direct
 * policy that means the same thing, and both are sent.
 *
 * Sending only `pending_dispatch_tasks_policy`, as this used to, made
 * the copy false in the case that matters most: an operator picking
 * "Cancel" to stop the robot got direct tasks left on upstream's own
 * `complete` default, i.e. a robot that keeps working after being stood
 * down. `reassign` has no direct-task counterpart — nothing can take a
 * direct task off this robot — so it maps to `complete`, which is what
 * the option's hint now says out loud.
 */
const PENDING_POLICIES: ReadonlyArray<{
  value: PendingDispatchTasksPolicy;
  direct: PendingDirectTasksPolicy;
  label: string;
  hint: string;
}> = [
  {
    value: 'reassign',
    direct: 'complete',
    label: 'Reassign (default)',
    hint: 'Queued dispatched tasks move to another robot in this fleet. Queued direct tasks cannot be handed over — this robot completes them first.',
  },
  {
    value: 'complete',
    direct: 'complete',
    label: 'Complete',
    hint: 'This robot works through everything already queued to it — dispatched and direct — before standing down.',
  },
  {
    value: 'cancel',
    direct: 'cancel',
    label: 'Cancel',
    hint:
      'Queued dispatched and direct tasks are dropped, and the task being executed now is ' +
      'cancelled -- including while the robot is in or boarding a lift car.',
  },
];

/**
 * Advisory pre-flight for putting a robot back into service. The
 * server enforces NONE of this — RMF will happily recommission a robot
 * that is lost, flat, or boxed in. It gates the confirm button so the
 * operator has to look at the robot, and it is labelled as such rather
 * than dressed up as a system check.
 */
const RECOMMISSION_CHECKLIST: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'localized', label: 'Robot is localized on the map' },
  { id: 'charged', label: 'Battery is adequately charged for the next task' },
  { id: 'clear', label: 'Workspace around the robot is clear' },
];

/** The direct-task policy that means the same thing as the operator's
 *  dispatched-task choice. Defaulting to `complete` — upstream's own
 *  default — keeps an unrecognised value from silently cancelling work. */
function directPolicyFor(value: PendingDispatchTasksPolicy): PendingDirectTasksPolicy {
  return PENDING_POLICIES.find((p) => p.value === value)?.direct ?? 'complete';
}

/**
 * Human labels for `CommandRecord.state` — the same map the door and
 * lift drawers carry (plan §7.3: "show accepted versus observed
 * completion" rather than treating the POST response as done).
 *
 * `accepted` is NOT success. The worker returns it when it published
 * to `task_api_requests` and got back no usable reply — no reply at
 * all, unparseable JSON, or a shape it does not recognise
 * (`worker/rmf_commands.py::handle_robot`). Nothing has observed RMF
 * act on the request at that point. `observed_applied` is the only
 * state that means RMF confirmed it.
 */
const COMMAND_STATE_LABEL: Record<string, string> = {
  received: 'Received',
  authorized: 'Authorized',
  // "Published to worker" named a process; the hotel reads "Sent" (P9).
  published: 'Sent',
  accepted: 'Accepted — awaiting confirmation',
  observed_applied: 'Applied',
  rejected: 'Rejected',
  timed_out: 'Timed out',
  outcome_unknown: 'Outcome unknown',
};

/** Only `observed_applied` earns the settled colour; everything still
 *  in flight is amber so it does not read as done. */
const COMMAND_STATE_KIND: Record<string, StatusKind> = {
  received: 'idle',
  authorized: 'idle',
  published: 'idle',
  accepted: 'warning',
  observed_applied: 'completed',
  rejected: 'error',
  timed_out: 'error',
  outcome_unknown: 'warning',
};

/**
 * Operator-visible text for a failed commission request.
 *
 * `ApiClientError` reads `message`/`code` off the top level of the
 * parsed response body, but the api-server raises
 * `HTTPException(detail={"code": …, "message": …})` (`api/robots.py`,
 * `api/guards.py`, `api/commands.py::_authorize_and_publish`), and
 * FastAPI serialises that as `{"detail": {…}}` — the v1 `ApiError`
 * envelope NESTED one level down. So the top-level lookup missed on
 * every real failure and the constructor's `'api error'` placeholder
 * was what the operator actually read: the same three words for a
 * capability denial, a worker that is unreachable, an epoch fence and
 * a malformed request alike. Read the envelope back out of `detail`.
 *
 * The fix belongs in `ApiClientError` itself — every screen in the SPA
 * has this bug — but that constructor is shared surface; this keeps
 * the drawer truthful without changing what other screens render.
 */
function commissionErrorText(error: unknown): string {
  // A request that never left the browser, said as that and not as a
  // refusal. This is the 2026-08-31 toggle failure: the api-server's
  // CORS `allow_methods` had no `PUT`, the browser refused the
  // preflight, and this line rendered the browser's own three words —
  // "Failed to fetch" — in the same red text that carries an
  // api-server verdict. Nothing about it said the api-server had never
  // been asked, so that is where the search went.
  //
  // The classification is `networkFailureText`'s, in the api layer, so
  // it is the same judgement everywhere; the only thing local to this
  // drawer is that the sentence gets NO `(code, HTTP nnn)` suffix,
  // because there is no code and no status to name.
  const blocked = networkFailureText(error);
  if (blocked) return blocked;
  if (!(error instanceof ApiClientError)) {
    return error instanceof Error && error.message ? error.message : 'Command failed';
  }
  // Typed `Record<string, unknown>`, but FastAPI also emits a bare
  // string `detail` (`HTTPException(detail="…")`), so neither shape
  // can be assumed here.
  const detail: unknown = error.detail;
  const envelope =
    typeof detail === 'object' && detail !== null ? (detail as Record<string, unknown>) : undefined;
  const nestedMessage = typeof envelope?.message === 'string' ? envelope.message : undefined;
  const nestedCode = typeof envelope?.code === 'string' ? envelope.code : undefined;
  const message =
    nestedMessage ?? (typeof detail === 'string' ? detail : undefined) ?? error.message;
  return `${message} (${nestedCode ?? error.code}, HTTP ${error.status})`;
}

export function RobotDetailDrawer({
  robot,
  onClose,
}: {
  robot: RobotState;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLElement>(null);
  useDismissOnOutside(panelRef, onClose);

  // Live-refetched task list, not a snapshot — `robot.taskId` can
  // change (task completes, a new one is assigned) for as long as
  // this drawer stays open, same reasoning as `DevicesScreen.tsx`'s
  // name-based door/lift selection.
  const tasksQuery = useTasks();
  const currentTask = robot.taskId
    ? (tasksQuery.data ?? []).find((t) => t.id === robot.taskId)
    : undefined;

  const liftClosed = useClosedLiftScopes();
  const noRoute = currentTask && hasNoRoute(currentTask) ? noRouteWords(liftClosed) : null;

  // WS6: is this robot in or boarding a car, and can we even tell? Both
  // feed the decommission dialog's warning; NEITHER gates the server.
  // The user chose (action plan §3e) that a decommission cancels the task
  // and releases the car with the robot inside, so the console's job is
  // to say so clearly, once, before the press -- not to refuse.
  const lifts = useLifts().data ?? [];
  const decommissionLift = heldLift(robot, lifts);

  // ---- What this drawer may honestly say (twin slice 1, W3 step 14) ----
  //
  // The robot's presence -- the same classification the map draws -- and
  // this page's own receipt age decide every line below; the record alone
  // is never trusted to be current (`robot-lines.ts`).
  const siteId = useSafeSiteId();
  const presenceKey = robotReceiptKey(robot.fleet, robot.name);
  const presence = useRobotPresence([robot], lifts).get(presenceKey);
  const feed = useFeedHealth(siteId);
  const { book: receipts } = useRobotReceipts(siteId);
  const receipt = receipts.get(presenceKey);
  const heardFacts = {
    agoMs: receipt.lastAt === null ? null : receipts.now() - receipt.lastAt,
    count: receipt.count,
  };
  // "heard N s ago" ticks every second while it is in seconds, then on
  // the minute; the wall clock it returns names the day for the times.
  const wallNowMs = useNowTick(heardTickMs(heardFacts));
  const { sites } = useActiveSite();
  const zone = resolveSiteTimeZone(sites, siteId);
  const siteZone = zone.source === 'site' ? zone.timeZone : null;
  const heard = heardText(presence, heardFacts);

  // The robot's own pose, when presence draws it at the record. Anything
  // else -- a roster stub, the (0, 0) sentinel, a ghost -- says nothing
  // about where it is now, however fresh the worker's re-stamp of the
  // record looks, so the decommission dialog must call occupancy unknown.
  const ownPose = presence?.pose?.source === 'live' ? presence.pose : null;
  // How old its own position is, on THIS device's clock: the receipt age
  // W3 keeps (`robot-receipts`, monotonic), and only once it is vouched
  // for -- from the second receipt, `heardText`'s rule: a first record
  // may be a replay of an old row, and would read as just heard. Never
  // `Date.now() - lastSeen`: a server stamp subtracted from this device's
  // clock is no age at all (contract §2; copy deck v2, N6).
  const ownPoseAgeMs =
    heardFacts.count >= 2 &&
    heardFacts.agoMs !== null &&
    Number.isFinite(heardFacts.agoMs) &&
    heardFacts.agoMs >= 0
      ? heardFacts.agoMs
      : null;
  const decommissionOccupancyUnknown =
    !decommissionLift &&
    (!ownPose ||
      !robot.location ||
      ownPoseAgeMs === null ||
      ownPoseAgeMs > ROBOT_STATE_MAX_AGE_MS);
  // Said as a time of day, never an age: the time of its last REAL record
  // on the site's clock -- the drawer's own "Last report" (`lastReportAtMs`):
  // the record's stamp while it reports, its ghost's when it is lost, and
  // never a stub's re-stamp.
  const lastPositionAtMs = lastReportAtMs(robot, presence);
  const decommissionStateAgeText =
    lastPositionAtMs !== null
      ? `from ${formatClock(lastPositionAtMs, siteZone, wallNowMs)}`
      : 'not available';

  const capabilities = useQuery(capabilitiesQueryOptions(siteId ? (siteId as SiteId) : null));
  const capabilityMap = new Map(
    (capabilities.data?.capabilities ?? []).map((c) => [c.capability, c]),
  );
  const commissionStates = useRobotCommissionStates();
  const commissionInfo = commissionStates.data?.get(commissionKey(robot.fleet, robot.name));
  const commissionState: RobotCommissionState = commissionInfo?.state ?? 'unknown';
  /** RMF's live `idle_behavior` flag. `null` means the fleet adapter
   *  published no commission object at all — not "on". */
  const idleBehavior: boolean | null = commissionInfo?.idleBehavior ?? null;
  /**
   * The fleet adapter is holding this robot after a fire alarm and is
   * asking a person to confirm where it is. Read from the registry
   * row's `issues`, which is the ONLY signal: the hold does not change
   * the robot's status, so a held robot reads as idle everywhere else.
   */
  const heldAfterFire = (commissionInfo?.issues ?? []).includes(FIRE_RELOCALIZE_ISSUE);
  /** Paused after its e-stop handed a lift back with it inside (fleet
   *  adapter >= 0.7.19): it resumes by itself only once re-localized (a QR
   *  scan or a new floor map) -- 0.7.20 removed the 5-minute auto-resume --
   *  or when someone presses the release: ops' "Confirm floor and release",
   *  hotel staff's "Scanned it" (2026-10-12). */
  const pausedAfterEstopLift = (commissionInfo?.issues ?? []).includes(ESTOP_RELOCALIZE_ISSUE);
  /** True only when `idle_behavior` is the ONE flag that is off.
   *  Automatic charging is the only partial commission this console
   *  writes, but `robot_commission_request` is open to anything that
   *  can reach the task API, so a robot can arrive refusing direct or
   *  dispatched work too — and the auto-charge chip below does not
   *  explain that robot. Both flags must be a published `true`; a
   *  `null` is a publisher that omitted them, which is exactly the
   *  "nothing settled" the paragraph is there to say. */
  const idleIsTheOnlyFalseFlag =
    idleBehavior === false &&
    commissionInfo?.dispatchTasks === true &&
    commissionInfo?.directTasks === true;
  const commission = useCommissionRobot();

  const { roles } = useAuth();
  // Who reads this drawer's words. `audienceFor`, never a role list of
  // our own: a mixed account reads ops, and `site_supervisor` and
  // `service_operator` read site (amendment A2). Ops get the Details
  // below; the decommission dialog's lift warnings are split by it.
  const audience = audienceFor(roles);
  const positionDetail = audience === 'ops' ? mapPositionDetail(presence) : null;
  const idlePolicies = useRobotIdlePolicies();
  /** A row exists only while automatic charging is OFF, and
   *  `idle_enabled` is false on every row the api-server writes. The
   *  flag is still read, because that is how the reconciler reads the
   *  same rows (`_observe` treats `row.idle_enabled` as "nothing
   *  desired"), and a console that disagreed with it would offer
   *  "Turn on…" for a robot the api-server considers already on. */
  const idlePolicy: RobotIdlePolicy | null =
    (idlePolicies.data ?? []).find(
      (p) =>
        p.robot.fleet === robot.fleet && p.robot.robot === robot.name && !p.idle_enabled,
    ) ?? null;
  const idlePoliciesSettled = idlePolicies.data !== undefined;
  const setIdleBehavior = useSetRobotIdleBehavior();
  // The site's zone, or the browser's when the site declares none: the
  // calendar's rule (`resolveSiteTimeZone`).
  const chip = autoChargeChip(idlePolicy, idleBehavior, idlePoliciesSettled, zone.timeZone);

  const [dialog, setDialog] = useState<Dialog>(null);
  /** Gates ONLY the local button. There is no server-side equivalent and
   *  there must not be: the decision is "warn, do not block". */
  const [strandAcknowledged, setStrandAcknowledged] = useState(false);
  const [pendingPolicy, setPendingPolicy] = useState<PendingDispatchTasksPolicy>('reassign');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [ttlMinutes, setTtlMinutes] = useState<number | null>(AUTO_CHARGE_DEFAULT_TTL_MINUTES);
  const [idleReason, setIdleReason] = useState('');

  function openDialog(next: NonNullable<Dialog>) {
    setPendingPolicy('reassign');
    setChecked({});
    setTtlMinutes(AUTO_CHARGE_DEFAULT_TTL_MINUTES);
    setIdleReason('');
    commission.reset();
    setIdleBehavior.reset();
    setDialog(next);
  }

  function confirm() {
    if (dialog === 'decommission') {
      commission.mutate({
        fleet: robot.fleet,
        robot: robot.name,
        action: 'decommission',
        // Both policies, always. Sent even where one equals upstream's
        // own default, so the audit row records the policy the operator
        // actually chose rather than leaving it indistinguishable from
        // "never asked" — and so the request says exactly what the
        // dialog promised for BOTH queues (see `PENDING_POLICIES`).
        pendingDispatchTasksPolicy: pendingPolicy,
        pendingDirectTasksPolicy: directPolicyFor(pendingPolicy),
      });
    } else if (dialog === 'commission') {
      commission.mutate({ fleet: robot.fleet, robot: robot.name, action: 'commission' });
    } else if (dialog === 'autocharge-off') {
      setIdleBehavior.mutate({
        fleet: robot.fleet,
        robot: robot.name,
        enabled: false,
        ttlMinutes,
        reason: idleReason,
      });
    } else if (dialog === 'autocharge-on') {
      // No TTL and no reason: turning it back on deletes the stored
      // row outright, and there is nothing to time-box.
      setIdleBehavior.mutate({ fleet: robot.fleet, robot: robot.name, enabled: true });
    }
    setDialog(null);
  }

  const checklistComplete = RECOMMISSION_CHECKLIST.every((item) => checked[item.id]);

  return (
    <aside ref={panelRef} className="nx-robot-drawer" role="dialog" aria-label={`Robot ${robot.name} details`}>
      <header className="nx-robot-drawer__head">
        <h2>{robot.name}</h2>
        {/* A link to the Overview, pointed at this robot -- always by
            fleet and name, never a drawer. */}
        <ShowWhereLink focus={{ kind: 'robot', fleetName: robot.fleet, robotName: robot.name }} />
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      {/* The drawer is a fixed 100vh flex column and its body has no
          scroll rule of its own, so every section added here pushes
          the ones below it off the bottom of the viewport with no way
          to reach them. `minHeight: 0` is what lets a flex child
          actually shrink below its content size. */}
      <div className="nx-robot-drawer__body" style={{ overflowY: 'auto', minHeight: 0 }}>
        <div className="nx-robot-drawer__row">
          <span>Status</span>
          {needsLocalising(robot, presence) ? (
            <span data-testid="robot-localize-chip">
              <StatusChip status="warning" label={LOCALIZE_CHIP_LABEL} title={LOCALIZE_CHIP_TITLE} />
            </span>
          ) : (
            <StatusChip
              status={ROBOT_STATUS_MAP[robot.status] ?? 'offline'}
              // Pulses only while the robot is reporting now: a pulsing
              // ACTIVE on a robot nobody has heard from is stale looking live.
              pulse={robot.status === 'active' && isReportingNow(presence, feed.clamped)}
            />
          )}
        </div>
        <div className="nx-robot-drawer__row">
          <span>Commission</span>
          <span data-testid="robot-commission-badge">
            <CommissionBadge state={commissionState} />
          </span>
        </div>
        <div className="nx-robot-drawer__row">
          <span>Fleet</span>
          <span>{robot.fleet}</span>
        </div>
        <div className="nx-robot-drawer__row">
          <span>Vendor</span>
          <span>{robot.vendor ?? '—'}</span>
        </div>
        <div className="nx-robot-drawer__row">
          <span>Battery</span>
          <span data-testid="robot-battery">{batteryText(robot.battery, presence, feed.clamped)}</span>
        </div>
        <div className="nx-robot-drawer__row">
          <span>Location</span>
          <span data-testid="robot-location">{locationText(presence, siteZone, wallNowMs)}</span>
        </div>
        <div className="nx-robot-drawer__row">
          <span>Last report</span>
          <span data-testid="robot-last-report">
            {lastReportText(robot, presence, siteZone, wallNowMs)}
            {heard ? ` · ${heard}` : ''}
          </span>
        </div>
        {/* Ops only, and collapsed: the robot map's coordinates, labelled
            as map units. A site audience never sees coordinates, and the
            Location row above names the floor only (`robot-lines.ts`). */}
        {positionDetail && (
          <details className="nx-robot-drawer__details" data-testid="robot-details">
            <summary>Details</summary>
            <div className="nx-robot-drawer__row">
              <span>{positionDetail.label}</span>
              <span data-testid="robot-details-position">{positionDetail.value}</span>
            </div>
          </details>
        )}

        {/* ABOVE service state, and outside the commission RoleGuard:
            a robot held after a fire alarm looks idle and healthy, so
            this is the only thing on the screen that says it is stuck
            — and the operator who can see it may be an foh_operator who
            never gets the commission section at all. */}
        {heldAfterFire && (
          <section
            className="nx-robot-drawer__section"
            data-testid="fire-hold-section"
            data-audience={audience}
          >
            <h3>Held after fire alarm</h3>
            {audience === 'ops' ? (
              <p className="nx-robot-drawer__sub">
                This robot was interrupted by a fire alarm and is waiting for someone to
                confirm where it is. RMF will resume from the position the robot is
                reporting, so check the robot is on the floor the console shows before
                releasing it. If it is somewhere else, relocalize it with the vendor app
                first.
              </p>
            ) : (
              <FireHoldSiteWords />
            )}
            <FireRelocalizeAction fleet={robot.fleet} robot={robot.name} />
          </section>
        )}

        {pausedAfterEstopLift && !heldAfterFire && audience === 'ops' && (
          <section className="nx-robot-drawer__section" data-testid="estop-lift-pause-section">
            <h3>Paused after its e-stop handed back a lift</h3>
            <p className="nx-robot-drawer__sub">
              Its e-stop held a lift, so the lift was handed back to guests with the robot in it
              or its doorway, and its job is paused. It resumes by itself only once its e-stop
              is clear and staff have re-localized it (scanned it at a QR point), and it has
              then stood still for 10 seconds. If staff have re-localized it where it stands,
              release it now.
            </p>
            <FireRelocalizeAction fleet={robot.fleet} robot={robot.name} />
          </section>
        )}

        {/* The hotel's half of the same pause (Akash, 2026-10-12): the
            staff who push it to a QR point and scan it there press
            "Scanned it" -- the same `fire_relocalize` the ops section
            sends -- so its job carries on. Hotel words only. Who may
            press is `ROBOT_FIRE_RELOCALIZE_ROLES` (inside the action);
            anyone else reads the procedure without the button, which
            still ends the pause by itself once the robot is back on the
            map and has stood still.
            The steps start where the robot is: still in the car or its
            doorway, its red estop button still pressed (this section only
            appears after the button held a lift). So out of the lift
            first -- released, then manual mode at once, then pushed onto
            the landing (D20, D44, D33) -- and only then the QR steps. They
            used to open on "Switch it to manual mode", which can't be done
            with the button pressed, and mentioned the release only after
            the list (review BC-F1, 2026-10-31). The words are the
            manual's C1.7 steps 4-8. */}
        {pausedAfterEstopLift && !heldAfterFire && audience === 'site' && (
          <section
            className="nx-robot-drawer__section"
            data-testid="estop-lift-pause-section"
            data-audience="site"
          >
            <h3>Paused: it needs to be put back on the map</h3>
            <p className="nx-robot-drawer__sub">
              Its red estop button was pressed while it was in a lift, so the lift went back to
              guests with the robot still in the car or its doorway, and its job is paused. Get it
              out of the lift and put it back on the map:
            </p>
            <ol className="nx-robot-drawer__steps" data-testid="estop-lift-pause-qr-steps">
              {OUT_OF_LIFT_THEN_QR.map((step) => (
                <li key={step.id}>{step.text}</li>
              ))}
            </ol>
            <RoleGuard
              allowedRoles={[...ROBOT_FIRE_RELOCALIZE_ROLES]}
              fallback={
                <p className="nx-robot-drawer__sub" data-testid="estop-lift-pause-no-press">
                  It may carry on its job by itself about 10 seconds after it is back on the map.
                  If it still doesn’t know where it is, call Nexsoss.
                </p>
              }
            >
              <p className="nx-robot-drawer__sub">
                Then press {SCANNED_IT_BUTTON}: its job carries on straight away. Without the
                press it may carry on by itself about 10 seconds after it is back on the map. Press
                it only after scanning. If it still doesn’t know where it is, call Nexsoss.
              </p>
            </RoleGuard>
            <FireRelocalizeAction fleet={robot.fleet} robot={robot.name} variant="scanned_it" />
          </section>
        )}

        <RoleGuard allowedRoles={[...COMMISSION_ROLES]}>
          <section className="nx-robot-drawer__section">
            <h3>Service state</h3>
            {/* `unknown` covers both "nothing reported" and "reported a
                mixed set of flags" — see `rmf_bridge.py::
                commission_state`, which is the only thing that decides
                between the three. Neither case can be called
                commissioned or decommissioned, and both need both
                actions offered, which is why one badge serves them. */}
            {/* Suppressed when the robot is `unknown` only because its
                automatic charging is off. `commission_state` collapses
                `(true, true, false)` to `unknown` and must keep doing
                so (fire mode's sweep keys on `decommissioned`), but
                once this drawer can say "automatic charging: off" out
                loud, telling the operator that nothing is settled is
                the less true of the two statements. The auto-charge
                control is the only thing that writes a partial
                commission, so a false `idle_behavior` is the whole
                explanation only while the other two flags are a
                published `true`. */}
            {commissionState === 'unknown' && !idleIsTheOnlyFalseFlag && (
              <p className="nx-robot-drawer__empty">
                This site&apos;s fleet registry reports no settled commission state for{' '}
                {robot.name} — either it has published none at all, or {robot.name} is
                accepting some kinds of work and not others. Both actions are offered; neither
                is known to be a no-op.
              </p>
            )}
            <div className="nx-task-drawer__actions">
              {commissionState !== 'decommissioned' && (
                <CommissionButton
                  capability="robot.decommission"
                  capabilityMap={capabilityMap}
                  capabilitiesLoaded={capabilities.data !== undefined}
                  pending={commission.isPending}
                  className="nx-task-drawer__cancel"
                  // The dialog's Cancel policy cancels the current task too
                  // (action plan §3e); the hover said it keeps running (P6).
                  hint="Stand the robot down: it stops taking new tasks and stops going back to its charger by itself. Its current task keeps running unless you choose Cancel."
                  label="Decommission"
                  testId="robot-decommission-btn"
                  onClick={() => openDialog('decommission')}
                />
              )}
              {commissionState !== 'commissioned' && (
                <CommissionButton
                  capability="robot.commission"
                  capabilityMap={capabilityMap}
                  capabilitiesLoaded={capabilities.data !== undefined}
                  pending={commission.isPending}
                  className="nx-task-drawer__action"
                  hint="Return the robot to service: dispatched and direct tasks are re-enabled. Automatic charging follows this robot's own auto-charge setting — if it is switched off, it stays off."
                  label="Recommission"
                  testId="robot-recommission-btn"
                  onClick={() => openDialog('commission')}
                />
              )}
            </div>
            {commission.isError && (
              <span
                className="nx-task-drawer__error"
                role="alert"
                data-testid="robot-commission-error"
              >
                {commissionErrorText(commission.error)}
              </span>
            )}
            {commission.data && (
              <CommissionOutcome command={commission.data.command} robotName={robot.name} />
            )}

            {/* Hidden entirely on a decommissioned robot: all three
                commission flags are false there, so automatic charging
                is off as a CONSEQUENCE of the stand-down. Showing an
                OFF chip beside the DECOMMISSIONED badge would invite
                the operator to "fix" it with a control that only moves
                one flag and would leave the robot exactly as stood
                down as before. */}
            {commissionState !== 'decommissioned' && (
              <RoleGuard allowedRoles={[...ROBOT_IDLE_BEHAVIOR_ROLES]}>
                <div data-testid="robot-autocharge">
                  <h4 className="nx-robot-drawer__subhead">Automatic charging</h4>
                  <div className="nx-robot-drawer__row">
                    <span>State</span>
                    <span data-testid="robot-autocharge-chip">
                      <StatusChip status={chip.status} label={chip.label} pulse={chip.pulse} />
                    </span>
                  </div>
                  {idlePolicy && robot.status === 'idle' && (
                    <p className="nx-robot-drawer__empty" data-testid="robot-autocharge-idle-note">
                      While automatic charging is off it reads IDLE, even when it is sitting on
                      its charger. It only reads CHARGING while it runs its own charge task, and
                      this setting stops that task.
                    </p>
                  )}
                  <div className="nx-task-drawer__actions">
                    {!idlePoliciesSettled ? null : idlePolicy ? (
                      <CommissionButton
                        capability="robot.set_idle_behavior"
                        capabilityMap={capabilityMap}
                        capabilitiesLoaded={capabilities.data !== undefined}
                        pending={setIdleBehavior.isPending}
                        className="nx-task-drawer__action"
                        hint="Let the robot drive itself back to its charger when it has nothing to do, and restore its low-battery return."
                        label="Turn on…"
                        testId="robot-autocharge-on-btn"
                        onClick={() => openDialog('autocharge-on')}
                      />
                    ) : (
                      <CommissionButton
                        capability="robot.set_idle_behavior"
                        capabilityMap={capabilityMap}
                        capabilitiesLoaded={capabilities.data !== undefined}
                        pending={setIdleBehavior.isPending}
                        className="nx-task-drawer__cancel"
                        hint="Stop the robot driving itself back to its charger. It keeps bidding for and running work."
                        label="Turn off…"
                        testId="robot-autocharge-off-btn"
                        onClick={() => openDialog('autocharge-off')}
                      />
                    )}
                  </div>
                  {setIdleBehavior.isError && (
                    <span
                      className="nx-task-drawer__error"
                      role="alert"
                      data-testid="robot-autocharge-error"
                    >
                      {commissionErrorText(setIdleBehavior.error)}
                    </span>
                  )}
                  {setIdleBehavior.data && (
                    <div data-testid="robot-autocharge-outcome">
                      <CommissionOutcome
                        command={setIdleBehavior.data.command}
                        robotName={robot.name}
                      />
                    </div>
                  )}
                </div>
              </RoleGuard>
            )}
          </section>
        </RoleGuard>

        <section className="nx-robot-drawer__section">
          <h3>Current task</h3>
          {!robot.taskId ? (
            <p className="nx-robot-drawer__empty">Not currently executing a task.</p>
          ) : tasksQuery.isLoading ? (
            <p className="nx-robot-drawer__empty">Loading…</p>
          ) : !currentTask ? (
            <p className="nx-robot-drawer__empty">
              Task <code>{robot.taskId}</code> is not in the live task list yet.
            </p>
          ) : (
            <>
              <div className="nx-robot-drawer__row">
                <span>Task ID</span>
                <span>
                  <code>{currentTask.id}</code>
                </span>
              </div>
              <div className="nx-robot-drawer__row">
                <span>Status</span>
                {/* The SAME chip the task drawer shows. Two screens
                    describing one task differently is how an operator
                    learns to trust neither. */}
                <StatusChip
                  status={TASK_STATUS_MAP[currentTask.status] ?? 'idle'}
                  pulse={currentTask.status === 'active' && !currentTask.attention}
                  label={
                    noRoute?.liftBlocked
                      ? 'waiting'
                      : currentTask.attention
                        ? `active · ${currentTask.attention.rmfStatus}`
                        : currentTask.status
                  }
                />
              </div>
              {noRoute?.liftBlocked && (
                <p className="nx-robot-drawer__sub" data-testid="robot-task-no-route" role="status">
                  {noRoute.sentence}
                </p>
              )}
              <div className="nx-robot-drawer__row">
                <span>Type</span>
                <span>{currentTask.type}</span>
              </div>
              <div className="nx-robot-drawer__row">
                <span>Triggered by</span>
                <span>{currentTask.triggeredBy ?? '—'}</span>
              </div>
              <div className="nx-robot-drawer__row">
                <span>Started</span>
                <span>{formatDateTime(currentTask.startedAt)}</span>
              </div>
              <div className="nx-robot-drawer__row">
                <span>Task time</span>
                <TaskElapsed task={currentTask} />
              </div>
              <div className="nx-robot-drawer__row">
                <span>Est. time remaining</span>
                <TaskRemaining task={currentTask} />
              </div>

              <h4 className="nx-robot-drawer__subhead">Waypoints</h4>
              <TaskPhasesList phases={currentTask.phases} />

              <TaskControlActions task={currentTask} robot={robot} lifts={lifts} />
              {/* A dustbag task PARKS the robot at its station and
                  waits to be told the bag was emptied — for the task's
                  wait (the site's default, 30 minutes unless the site
                  set another, or what the dispatch form was given),
                  then it completes anyway flagged "not confirmed".
                  This is the press that ends that wait, and it sits
                  here because the Fleet screen is where an operator
                  who has just emptied the bag is looking: they went to
                  find the robot, not the task. */}
              {isConfirmableDustbagTask(currentTask) && (
                <DustbagConfirmAction
                  fleet={robot.fleet}
                  robot={robot.name}
                  taskId={currentTask.id}
                />
              )}
            </>
          )}
        </section>
      </div>

      {dialog === 'decommission' && (
        <FormDialog
          title={`Decommission ${robot.name}?`}
          confirmLabel="Decommission"
          variant="danger"
          confirmDisabled={
            (decommissionLift !== undefined || decommissionOccupancyUnknown) &&
            !strandAcknowledged
          }
          onCancel={() => setDialog(null)}
          onConfirm={confirm}
          testId="robot-decommission-dialog"
        >
          <ul style={DIALOG_LIST}>
            <li>
              {robot.name} stops accepting new dispatched tasks, new direct tasks, and its own
              idle behaviours — it will not drive itself back to a charger.
            </li>
            {/* A site audience reads no "RMF" and no task ids (plan §2
                doctrine); the facts are the same. */}
            {audience === 'site' ? (
              <li>
                {/* No raw fleet id for a site reader (copy deck v2, N8). */}
                Queued <strong>dispatched</strong> tasks are redistributed{' '}
                <strong>within its own fleet only</strong> — they can&apos;t be moved to a robot in
                another fleet, and a <strong>direct</strong> task (one addressed to {robot.name} by
                name) can&apos;t be moved to any robot at all.
              </li>
            ) : (
              <li>
                Queued <strong>dispatched</strong> tasks are redistributed{' '}
                <strong>within the {robot.fleet} fleet only</strong> — RMF cannot move them to a
                robot in another fleet, and it cannot move a <strong>direct</strong> task (one
                addressed to {robot.name} by name) to any robot at all.
              </li>
            )}
            <li>
              {currentTask && audience === 'site' ? (
                <>
                  If you choose <strong>Cancel</strong>, the task {robot.name} is executing now is
                  cancelled too. <strong>Reassign</strong> and <strong>Complete</strong> leave it
                  running.
                </>
              ) : currentTask ? (
                <>
                  If you choose <strong>Cancel</strong>, the task {robot.name} is executing now (
                  <code>{currentTask.id}</code>) is cancelled too. <strong>Reassign</strong> and{' '}
                  <strong>Complete</strong> leave it running.
                </>
              ) : (
                <>
                  If you choose <strong>Cancel</strong> and {robot.name} picks up a task before
                  you confirm, that task is cancelled too. <strong>Reassign</strong> and{' '}
                  <strong>Complete</strong> leave it running.
                </>
              )}
            </li>
          </ul>

          {/* WARNS, AND DOES NOT BLOCK. The user chose (action plan §3e),
              against an explicit warning, that a decommission cancels the
              task and releases the car even with the robot inside: the
              car goes back to public service and its doors may open for
              guests at other floors. The platform's obligations are this
              block, the checkbox below, and a loud ERROR in the worker --
              never a refusal, here or server-side. */}
          {/* SPLIT BY AUDIENCE, as the task drawer's in-lift banner is
              (plan W5 step 7; amendment A2). The push and Recover are
              Nexsoss's procedure, not the hotel's: a site audience --
              `site_supervisor` here, the one hotel role that holds
              `robot.decommission` -- is told not to, and to call Nexsoss.
              Still a warning, not a block: the checkbox below gates the
              button for both audiences exactly as before. */}
          {decommissionLift && audience === 'site' && (
            <div className="nx-robot-drawer__danger" data-testid="decommission-in-lift-warning" role="alert">
              <strong>{robot.name} is in or at lift {decommissionLift.name}.</strong>
              <p>
                Don&apos;t decommission it while it is in or at {decommissionLift.name}. Call
                Nexsoss. If you go ahead, its task is cancelled and the car goes back into service
                with {robot.name} still inside; its doors may open for guests on other floors.
              </p>
            </div>
          )}
          {decommissionLift && audience === 'ops' && (
            <div className="nx-robot-drawer__danger" data-testid="decommission-in-lift-warning" role="alert">
              <strong>{robot.name} is in or boarding lift {decommissionLift.name}.</strong>
              <p>
                Decommissioning now cancels {currentTask ? <code>{currentTask.id}</code> : 'its task'}{' '}
                <strong>and hands the car back to public service while {robot.name} is still
                inside</strong>; its doors may open for guests at other floors. After you confirm,
                go to the car: hold the door, immobilise the robot, push it at least 1.2 m onto the
                landing, then press <strong>Recover</strong> for {decommissionLift.name}.
              </p>
            </div>
          )}
          {!decommissionLift && decommissionOccupancyUnknown && audience === 'site' && (
            <div className="nx-robot-drawer__danger" data-testid="decommission-unknown-warning" role="alert">
              <strong>Whether {robot.name} is in a lift is not known.</strong>
              <p>
                Its last position is {decommissionStateAgeText}. Decommissioning now cancels its
                task anyway. If it may be in a lift, don&apos;t decommission it. Call Nexsoss.
              </p>
            </div>
          )}
          {!decommissionLift && decommissionOccupancyUnknown && audience === 'ops' && (
            <div className="nx-robot-drawer__danger" data-testid="decommission-unknown-warning" role="alert">
              <strong>Whether {robot.name} is in a lift car is unknown.</strong>
              <p>
                Its last position is {decommissionStateAgeText}. Decommissioning now cancels{' '}
                {currentTask ? <code>{currentTask.id}</code> : 'its task'} anyway. Check the car
                before anything else; if the robot is inside, use the lift procedure and press
                Recover.
              </p>
            </div>
          )}
          {(decommissionLift || decommissionOccupancyUnknown) && (
            <label style={DIALOG_OPTION} data-testid="decommission-strand-ack">
              <input
                type="checkbox"
                checked={strandAcknowledged}
                onChange={(e) => setStrandAcknowledged(e.target.checked)}
              />
              <span>
                I understand {robot.name} will be left in{' '}
                {decommissionLift ? decommissionLift.name : 'the car, if it is in one'} and the car
                returns to service.
              </span>
            </label>
          )}

          <fieldset style={DIALOG_FIELDSET}>
            <legend style={DIALOG_LEGEND}>Queued tasks</legend>
            {PENDING_POLICIES.map((policy) => (
              <label key={policy.value} style={DIALOG_OPTION}>
                <input
                  type="radio"
                  name="nx-commission-pending-policy"
                  value={policy.value}
                  checked={pendingPolicy === policy.value}
                  onChange={() => setPendingPolicy(policy.value)}
                />
                <span>
                  <span style={DIALOG_OPTION_LABEL}>{policy.label}</span>
                  <span style={DIALOG_OPTION_HINT}>{policy.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </FormDialog>
      )}

      {dialog === 'commission' && (
        <FormDialog
          title={`Recommission ${robot.name}?`}
          confirmLabel="Recommission"
          variant="primary"
          confirmDisabled={!checklistComplete}
          onCancel={() => setDialog(null)}
          onConfirm={confirm}
          testId="robot-recommission-dialog"
        >
          <p className="nx-confirm-dialog__message">
            {robot.name} will accept dispatched and direct tasks again. Automatic charging
            follows this robot&apos;s own auto-charge setting, so if that is switched off it
            stays off and {robot.name} will not drive itself to a charger. Work that was
            reassigned away when it was decommissioned does not come back.
          </p>
          <fieldset style={DIALOG_FIELDSET}>
            <legend style={DIALOG_LEGEND}>Operator checklist</legend>
            {RECOMMISSION_CHECKLIST.map((item) => (
              <label key={item.id} style={DIALOG_OPTION}>
                <input
                  type="checkbox"
                  checked={Boolean(checked[item.id])}
                  onChange={(e) =>
                    setChecked((prev) => ({ ...prev, [item.id]: e.target.checked }))
                  }
                />
                <span style={DIALOG_OPTION_LABEL}>{item.label}</span>
              </label>
            ))}
            {/* "The system", not "RMF": a site supervisor reads this
                (Akash, 2026-10-31: no "RMF" where the hotel sees). */}
            <p style={DIALOG_OPTION_HINT}>
              Nothing checks these but you. The system recommissions the robot whether or not
              any of it is true.
            </p>
          </fieldset>
        </FormDialog>
      )}

      {dialog === 'autocharge-off' && (
        <FormDialog
          title={`Turn off automatic charging for ${robot.name}?`}
          confirmLabel="Turn off"
          variant="danger"
          confirmDisabled={false}
          onCancel={() => setDialog(null)}
          onConfirm={confirm}
          testId="robot-autocharge-off-dialog"
        >
          <ul style={DIALOG_LIST}>
            <li>
              If {robot.name} is already on its way to the charger it will stop where it is.
              The request is <strong>refused while it is inside a lift</strong> — a trip
              cancelled in the car leaves the lift session held until something else moves the
              robot.
            </li>
            <li>
              Its <strong>low-battery auto-return is also off</strong> until charging is turned
              back on. {robot.name} will still refuse work its battery cannot finish, but it
              will not take itself home when it runs low.
            </li>
            <li>
              Work is unaffected: {robot.name} keeps bidding for dispatched tasks and accepting
              direct ones.
            </li>
          </ul>

          <fieldset style={DIALOG_FIELDSET}>
            <legend style={DIALOG_LEGEND}>Automatic charging turns itself back on after</legend>
            <select
              value={ttlMinutes === null ? '' : String(ttlMinutes)}
              onChange={(e) => setTtlMinutes(e.target.value === '' ? null : Number(e.target.value))}
              data-testid="robot-autocharge-ttl"
              aria-label="Automatic charging turns itself back on after"
            >
              {AUTO_CHARGE_TTL_CHOICES.map((choice) => (
                <option key={choice.minutes} value={String(choice.minutes)}>
                  {choice.label}
                </option>
              ))}
              {/* Open-ended is a platform-admin choice only, and the
                  api-server enforces that independently — drawing it
                  for anyone else produces a 400 they cannot act on. */}
              {roles.includes(OPEN_ENDED_TTL_ROLE) && (
                <option value="">Until turned back on</option>
              )}
            </select>
            <p style={DIALOG_OPTION_HINT}>
              When the timer runs out, charging switches back on by itself and {robot.name}
              {' '}starts moving as soon as it is idle. An alert is raised so nobody is
              surprised by it.
            </p>
          </fieldset>

          <fieldset style={DIALOG_FIELDSET}>
            <legend style={DIALOG_LEGEND}>Reason (optional)</legend>
            <input
              type="text"
              maxLength={200}
              value={idleReason}
              onChange={(e) => setIdleReason(e.target.value)}
              data-testid="robot-autocharge-reason"
              aria-label="Reason"
              placeholder="e.g. engineer working on the robot"
            />
          </fieldset>
        </FormDialog>
      )}

      {dialog === 'autocharge-on' && (
        <FormDialog
          title={`Turn automatic charging back on for ${robot.name}?`}
          confirmLabel="Turn on"
          variant="primary"
          confirmDisabled={false}
          onCancel={() => setDialog(null)}
          onConfirm={confirm}
          testId="robot-autocharge-on-dialog"
        >
          <p className="nx-confirm-dialog__message">
            {robot.name} will return to its charger as soon as it is idle — which may be
            immediately, and by way of the lifts. Its low-battery return is restored at the
            same time.
          </p>
        </FormDialog>
      )}
    </aside>
  );
}

/** The QR steps that put a robot back on the map (D35), numbered: the
 *  lost-robot card's own words (`QR_SCAN_STEPS`), so the panel and the
 *  card can't give two procedures. */
function QrScanStepList({ testId }: { testId: string }) {
  return (
    <ol className="nx-robot-drawer__steps" data-testid={testId}>
      {QR_SCAN_STEPS.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  );
}

/**
 * The fire hold in the hotel's words (batch B, Akash 2026-10-31: "Rename
 * and keep, QR route"). It used to read ops' paragraph to everyone: "RMF
 * will resume…", "relocalize it with the vendor app", which the hotel
 * doesn't have. The hotel releases its own robots (D26): press when it
 * stands on the floor the console shows, otherwise put it back on the map
 * with the QR steps first (D35), and call Nexsoss only when that fails.
 * Still one tap, no confirm (decided 2026-10-31): the check is the
 * sentence above the button.
 */
function FireHoldSiteWords() {
  // Where to look, the Location check before the press and the button
  // that blocks manual mode (review BC-F5, 2026-10-31; C1.3 steps 3, 5
  // and 6): the adapter holds only a robot that was in a lift when the
  // alarm started, and the press asserts the floor the robot reports, so
  // a scan on the wrong floor, or one that silently failed, would release
  // it on the wrong map.
  return (
    <>
      <p className="nx-robot-drawer__sub">
        This robot stopped for the fire alarm while it was in a lift, so it may still be in a car
        or on another floor. It is waiting for someone to check which floor it is on. Once it is
        released, it carries on from the floor shown in Location above.
      </p>
      <RoleGuard
        allowedRoles={[...ROBOT_FIRE_RELOCALIZE_ROLES]}
        fallback={
          <p className="nx-robot-drawer__sub" data-testid="fire-hold-no-press">
            The front desk or a supervisor releases it here once it is standing on that floor. If
            it is on another floor, or no floor is shown, put it back on the map first:
          </p>
        }
      >
        <p className="nx-robot-drawer__sub">
          If it is standing on that floor and its path is clear, press Confirm floor and release.
          If it is on another floor, or no floor is shown, put it back on the map first:
        </p>
      </RoleGuard>
      <p className="nx-robot-drawer__sub" data-testid="fire-hold-estop-first">
        If its red estop button is pressed, check nothing and nobody is in its way and twist the
        button to release it first: it can&apos;t be switched to manual mode while it is pressed.
      </p>
      <QrScanStepList testId="fire-hold-qr-steps" />
      <RoleGuard
        allowedRoles={[...ROBOT_FIRE_RELOCALIZE_ROLES]}
        fallback={<p className="nx-robot-drawer__sub">{STILL_LOST_CALL}</p>}
      >
        <p className="nx-robot-drawer__sub" data-testid="fire-hold-then-press">
          Once Location above shows the floor it is on, press Confirm floor and release.{' '}
          {STILL_LOST_CALL}
        </p>
      </RoleGuard>
    </>
  );
}

function CommissionButton({
  capability,
  capabilityMap,
  capabilitiesLoaded,
  pending,
  className,
  hint,
  label,
  testId,
  onClick,
}: {
  capability: CapabilityId;
  capabilityMap: Map<string, { enabled: boolean; disabled_reasons: string[] }>;
  capabilitiesLoaded: boolean;
  pending: boolean;
  className: string;
  hint: string;
  label: string;
  testId: string;
  onClick: () => void;
}) {
  // Same gate `TaskControlActions` applies: the button is only
  // disabled once the capability probe has actually answered, so a
  // slow/failed probe leaves the action available and the server
  // stays the authority on the refusal.
  const cap = capabilityMap.get(capability);
  const disabled = pending || (capabilitiesLoaded && cap?.enabled === false);
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      title={cap?.enabled === false ? cap.disabled_reasons.join(', ') : hint}
      data-testid={testId}
    >
      {pending ? 'Sending…' : label}
    </button>
  );
}

/**
 * What the command record actually says, in place of the bare enum
 * value this used to print.
 *
 * Three outcomes have to survive to the operator, and printing
 * `state` verbatim carried none of them:
 *
 *  - `observed_applied` — RMF confirmed the change. The only success.
 *  - `accepted` — published to RMF, nothing confirmed. Read as success
 *    only because it is a word that sounds like one.
 *  - `rejected` — possibly PARTIAL. RMF answers a
 *    `robot_commission_request` with a separate result per part (the
 *    commission change itself, and each of the two queued-task
 *    policies — `rmf_api_msgs/schemas/robot_commission_response.json`),
 *    and the worker reports the whole command rejected if ANY part
 *    reports failure (`rmf_commands.py::handle_robot`). So a robot
 *    that really did stand down, whose `pending_direct_tasks_policy`
 *    alone was refused, showed the operator nothing but "rejected" —
 *    inviting a retry of something already done. `outcome_message`
 *    names the failing parts and is the only place that distinction
 *    exists, so it is always shown.
 */
function CommissionOutcome({
  command,
  robotName,
}: {
  command: CommandRecord;
  robotName: string;
}) {
  return (
    <div data-testid="robot-commission-command-state">
      <StatusChip
        status={COMMAND_STATE_KIND[command.state] ?? 'idle'}
        label={COMMAND_STATE_LABEL[command.state] ?? command.state}
      />
      {command.outcome_message && (
        <p className="nx-task-drawer__sub" data-testid="robot-commission-outcome">
          {command.outcome_message}
        </p>
      )}
      {/* "The system", not "RMF", for every reader: a site supervisor
          decommissions too (Akash, 2026-10-31), and it is as true for ops. */}
      {command.state === 'accepted' && (
        <p className="nx-task-drawer__sub">
          Sent, but the system has not confirmed it. {robotName} may or may not have changed
          state — watch the Commission badge above rather than treating this as done.
        </p>
      )}
      {command.state === 'rejected' && (
        <p className="nx-task-drawer__sub">
          The system answers separately for the commission change and for each queued-task
          policy, so a rejection means at least one part failed — not necessarily all of them,
          and not necessarily the stand-down itself. Check the Commission badge above before
          retrying.
        </p>
      )}
    </div>
  );
}

